/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {FabMessageService} from '@fabric/components/message';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {DISMISS_MSG, VIEW_AUTHORITY} from '../../../../../common/constants';
import {LoadingCounter} from '../../../../../core/helpers/loading-counter';
import {StateService} from '../../../../../core/state/state.service';
import {WhiteSpaceInputValidator} from '../../../../../core/validators/white-space-input-validator';
import {EngProgClass} from '../../../../../models/eng-prog-class.model';
import {ForecastState} from '../../../../../models/forecast-state.model';
import {IndustryScenario} from '../../../../../models/industry-scenario-dto.model';
import {PropulsionType} from '../../../../../models/propulsion-type.model';
import {ScenarioCategory} from '../../../../../models/scenario-category.model';
import {ScenarioType} from '../../../../../models/scenario-type.model';
import {VehicleScenario} from '../../../../../models/vehicle-scenario-dto.model';
import {VehicleScenarioService} from '../../../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-edit-vehicle-scenario.component',
    templateUrl: './edit-vehicle-scenario-popup.component.html',
    styleUrls: ['./edit-vehicle-scenario-popup.component.scss'],
    standalone: false
})
export class EditVehicleScenarioPopupComponent implements OnInit {

  dataSource: MatTableDataSource<IndustryScenario> = new MatTableDataSource(null);
  readonly loading = new LoadingCounter();
  errorMessage = '';
  vehicleScenarioForm: FormGroup;
  vehicleScenario: VehicleScenario;

  scenarioCategories: ScenarioCategory[] = [];
  scenarioTypes: ScenarioType[] = [];
  engProgClasses: EngProgClass[] = [];
  propulsionTypes: PropulsionType[] = [];
  forecastStates: ForecastState[] = [];
  industryScenarios: IndustryScenario[] = [];
  filteredIndustryScenarios: Observable<IndustryScenario[]>;

  isAdmin: boolean;

  industryScenariosDropdown: any = {
    form: new FormControl(),
    filteredOptions: null,
    options: [],
    selectedOption: '%'
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VehicleScenario,
    public snackbar: FabMessageService,
    public dialogRef: MatDialogRef<any>,
    private readonly vehicleScenarioService: VehicleScenarioService,
    private readonly userService: StateService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  /**
   * Load scenario categories, scenario types, propulsion types, forecast states, engineering program classes,
   * and industry scenarios for the dropdowns
   * Make vehicleScenarioForm with whitespace check validation
   */
  ngOnInit(): void {
    this.isAdmin = this.userService.hasAuthority(VIEW_AUTHORITY.centralOfficeAdminAuthority);
    this.vehicleScenario = this.data;
    this.loadScenarioCategories();
    this.loadScenarioTypes();
    this.loadPropulsionTypes();
    this.loadForecastStates();
    this.loadEngProgClass();
    this.loadIndustryScenarios();
    this.vehicleScenarioForm = new FormGroup({
      updatedVehicleScenarioName: new FormControl(this.vehicleScenario.vehScenName, [Validators.required,
        WhiteSpaceInputValidator.noWhitespaceValidator]),
      updatedScenarioCategory: new FormControl(this.vehicleScenario.scenCat, [Validators.required]),
      updatedScenarioType: new FormControl(this.vehicleScenario.scenType, [Validators.required]),
      updatedPropulsionType: new FormControl(this.vehicleScenario.propulsionType, [Validators.required]),
      updatedForecastState: new FormControl(this.vehicleScenario.forecastState, [Validators.required]),
      updatedEngProgClass: new FormControl(this.vehicleScenario.engProgClass, [Validators.required]),
      updatedIndustryScenario: new FormControl(this.vehicleScenario.industryScenario, [Validators.required]),
      updatedAssumption: new FormControl(this.vehicleScenario.vehScenAsmptn, [Validators.required,
        WhiteSpaceInputValidator.noWhitespaceValidator]),
      updatedIsDeleteLocked: new FormControl(this.vehicleScenario.isDeleteLocked, [Validators.required]),
      updatedIsEditLocked: new FormControl(this.vehicleScenario.isEditLocked, [Validators.required]),
      updatedIsHiddenLocked: new FormControl(this.vehicleScenario.isHidden, [Validators.required])
    });
  }

  /**
   * Fetch a list of all the scenario categories from service
   */
  loadScenarioCategories(): void {
    this.loading.track(this.vehicleScenarioService.getAllScenarioCategories()).subscribe(
      (data) => {
        this.scenarioCategories = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the scenario types from service
   */
  loadScenarioTypes(): void {
    this.loading.track(this.vehicleScenarioService.getAllScenarioTypes()).subscribe(
      (data) => {
        this.scenarioTypes = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the Propulsion types from service
   */
  loadPropulsionTypes(): void {
    this.loading.track(this.vehicleScenarioService.getAllPropulsionTypes()).subscribe(
      (data) => {
        this.propulsionTypes = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the forecast states from service
   */
  loadForecastStates(): void {
    this.loading.track(this.vehicleScenarioService.getAllForecastStates()).subscribe(
      (data) => {
        this.forecastStates = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the engineering program class from service
   */
  loadEngProgClass(): void {
    this.loading.track(this.vehicleScenarioService.getAllEngProgClass()).subscribe(
      (data) => {
        this.engProgClasses = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Fetch a list of all the industry scenarios from service
   */
  loadIndustryScenarios(): void {
    let fcorg = '';
    if (this.vehicleScenario.fcorg === 'CHEV CAD EUROPE' || this.vehicleScenario.fcorg === 'OPEL/VAUXHALL') {
      fcorg = 'GME';
    } else if (this.vehicleScenario.fcorg === 'BUICKCHVCAD ISRAEL') {
      fcorg = 'GMI';
    } else {
      fcorg = this.vehicleScenario.fcorg.replace('-NONGM', '');
    }
    this.loading.track(this.vehicleScenarioService.getIndustryScenarios(fcorg)).subscribe(
      (data) => {
        this.industryScenarios = data;
      },
      (error) => {
        this.errorHandle(error);
      }
    );
  }

  /**
   * Create a filtered array of industry scenarios based on if the name or id contains the param value
   * If value is equivalent to the current vehicle scenario's industry scenario name,
   * return the industry scenario list
   * @param value the comparison string given from the industry scenario input field value
   */
  industryFilter(value: string): IndustryScenario[] {
    return this.industryScenarios.filter(industryScenario =>
      value === this.vehicleScenario.industryScenario.industryScenarioName ?
        industryScenario.industryScenarioName.includes('') :
        (industryScenario.industryScenarioName.includes(value) ||
          industryScenario.id.toString().includes(value))
    );

  }

  /**
   * Create a string of industry scenario id and industry scenario name
   * @param industryScenario industry scenario to display
   */
  displayIndustryScenarioValue(industryScenario: IndustryScenario): string {
    return industryScenario.id + ' ' + industryScenario.industryScenarioName;
  }

  /**
   * Set the input to an empty string when the industry scenario input box is clicked
   */
  resetValue(): void {
    this.vehicleScenarioForm.get('updatedIndustryScenario')
    .setValue({id: '', industryScenarioName: ''});

    this.filteredIndustryScenarios = this.vehicleScenarioForm.get('updatedIndustryScenario').valueChanges.pipe(
      startWith(''),
      map(value => this.industryFilter(value || '')),
    );
  }

  /**
   * Saves the all the updated values to vehicleScenario. calls updateVehicleScenario from service
   * with the updated vehicleScenario
   */
  onSubmit(): void {
    this.vehicleScenario.vehScenName = this.vehicleScenarioForm.get('updatedVehicleScenarioName').value;
    this.vehicleScenario.scenCat = this.vehicleScenarioForm
    .get('updatedScenarioCategory').value;
    this.vehicleScenario.scenType = this.vehicleScenarioForm.get('updatedScenarioType').value;
    this.vehicleScenario.vehScenAsmptn = this.vehicleScenarioForm
    .get('updatedAssumption').value;
    this.vehicleScenario.industryScenario = this.vehicleScenarioForm.get('updatedIndustryScenario').value;
    this.vehicleScenario.isDeleteLocked = this.vehicleScenarioForm.get('updatedIsDeleteLocked').value;
    this.vehicleScenario.isEditLocked = this.vehicleScenarioForm.get('updatedIsEditLocked').value;
    this.vehicleScenario.isHidden = this.vehicleScenarioForm.get('updatedIsHiddenLocked').value;
    if (this.vehicleScenario.fcorg === 'GMTVP') {
      this.vehicleScenario.propulsionType.id = this.vehicleScenarioForm.get('updatedPropulsionType').value;
      this.vehicleScenario.forecastState.id = this.vehicleScenarioForm.get('updatedForecastState').value;
      this.vehicleScenario.engProgClass.id = this.vehicleScenarioForm.get('updatedEngProgClass').value;
    }
    this.loading.track(this.vehicleScenarioService.updateVehicleScenario(this.vehicleScenario)).subscribe({
      error: (error) => {
        this.errorHandle(error);
      },
      complete: () => {
        this.snackbar.displaySuccess('Successfully updated vehicle scenario', DISMISS_MSG);
        this.dialogRef.close();

      }
    });
  }

  /**
   * On cancel close the dialog
   */
  onCancel(): void {
    this.dialogRef.close();
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.snackbar.displayError(error, DISMISS_MSG);
  }
}
